import { useState, useCallback, useEffect, useRef } from "react"
import { api } from "~/utils/api"
import { getNumberFormatLocale } from "~/stores/number-format-store"
import { getHakiTone, type HakiTone } from "~/stores/haki-tone-store"
import type {
  ActionResolution,
  HakiDebugInfo,
  PageContext,
  ToolStatus,
  TypedSuggestion,
} from "~/server/api/routers/haki/types"

interface ChatInput {
  threadId: number
  pageContext?: PageContext
  numberFormatLocale?: "de-DE" | "en-US"
  tone?: HakiTone
  source?: "chat" | "explain-forecast"
}

function resolveLocaleForServer(): "de-DE" | "en-US" {
  return getNumberFormatLocale() === "en-US" ? "en-US" : "de-DE"
}

function resolveToneForServer(): HakiTone {
  return getHakiTone()
}

interface UseHakiStreamOptions {
  timeoutMs?: number
  staleMs?: number
  onComplete?: (
    content: string,
    suggestions?: TypedSuggestion[],
    debugInfo?: HakiDebugInfo,
    blocks?: CommittedBlock[],
    reasoningLog?: ReasoningStep[],
  ) => void
  onError?: (content: string) => void
  isHakio?: boolean
}

export interface CommittedBlock {
  blockType: "chart" | "table" | "kpis" | "matrix" | "action" | "unavailable"
  blockId: string
  data: unknown
  afterLength?: number
  /** `action` blocks only — persisted by haki.report.recordActionResolution. */
  resolution?: ActionResolution
}

export interface ReasoningStep {
  type: "thinking" | "tool"
  content: string
  tool?: string
  detail?: string
  input?: Record<string, unknown>
}

interface UseHakiStreamReturn {
  send: (threadId: number, pageContext?: PageContext, source?: "chat" | "explain-forecast") => void
  streamingText: string
  toolStatus: ToolStatus | undefined
  thinkingText: string
  reasoningLog: ReasoningStep[]
  isStreaming: boolean
  isComplete: boolean
  stale: boolean
  finalContent: string | null
  finalSuggestions: TypedSuggestion[] | undefined
  committedBlocks: CommittedBlock[]
  debugInfo: HakiDebugInfo | null
  langfuseTraceId: string | null
  error: string | null
  cancel: () => void
  reset: () => void
}

export function useHakiStream(options: UseHakiStreamOptions = {}): UseHakiStreamReturn {
  const { timeoutMs = 120_000, staleMs = 30_000, onComplete, onError, isHakio = false } = options

  const [chatInput, setChatInput] = useState<ChatInput | null>(null)
  const [streamingText, setStreamingText] = useState("")
  const [toolStatus, setToolStatus] = useState<ToolStatus | undefined>()
  const [thinkingText, setThinkingText] = useState("")
  const [stale, setStale] = useState(false)
  const [finalContent, setFinalContent] = useState<string | null>(null)
  const [finalSuggestions, setFinalSuggestions] = useState<TypedSuggestion[] | undefined>()
  const [committedBlocks, setCommittedBlocks] = useState<CommittedBlock[]>([])
  const blocksRef = useRef<CommittedBlock[]>([])
  const [reasoningLog, setReasoningLog] = useState<ReasoningStep[]>([])
  const reasoningRef = useRef<ReasoningStep[]>([])
  const finalContentRef = useRef<string | null>(null)
  const followupsRef = useRef<TypedSuggestion[]>([])
  const [debugInfo, setDebugInfo] = useState<HakiDebugInfo | null>(null)
  const [langfuseTraceId, setLangfuseTraceId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const activeThreadIdRef = useRef<number | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const staleRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unloadingRef = useRef(false)

  useEffect(() => {
    const markUnloading = () => {
      unloadingRef.current = true
    }
    const clearUnloading = () => {
      unloadingRef.current = false
    }
    window.addEventListener("pagehide", markUnloading)
    window.addEventListener("beforeunload", markUnloading)
    window.addEventListener("pageshow", clearUnloading)
    return () => {
      window.removeEventListener("pagehide", markUnloading)
      window.removeEventListener("beforeunload", markUnloading)
      window.removeEventListener("pageshow", clearUnloading)
    }
  }, [])

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (staleRef.current) clearTimeout(staleRef.current)
    timeoutRef.current = null
    staleRef.current = null
  }, [])

  const resetTimers = useCallback(() => {
    clearTimers()
    setStale(false)
  }, [clearTimers])

  useEffect(() => clearTimers, [clearTimers])

  const startTimers = useCallback(() => {
    resetTimers()
    staleRef.current = setTimeout(() => setStale(true), staleMs)
    timeoutRef.current = setTimeout(() => {
      const content = "Haki took too long to respond. Please try again."
      setError(content)
      setStreamingText("")
      setToolStatus(undefined)
      setThinkingText("")
      setChatInput(null)
      onError?.(content)
    }, timeoutMs)
  }, [resetTimers, staleMs, timeoutMs, onError])

  const cancel = useCallback(() => {
    activeThreadIdRef.current = null
    resetTimers()
    const captured = streamingText
    setStreamingText("")
    setToolStatus(undefined)
    setThinkingText("")
    setChatInput(null)
    if (captured) {
      setFinalContent(captured)
    }
  }, [resetTimers, streamingText])

  const reset = useCallback(() => {
    activeThreadIdRef.current = null
    resetTimers()
    setChatInput(null)
    setStreamingText("")
    setToolStatus(undefined)
    setThinkingText("")
    setStale(false)
    setFinalContent(null)
    setFinalSuggestions(undefined)
    setCommittedBlocks([])
    blocksRef.current = []
    setReasoningLog([])
    reasoningRef.current = []
    finalContentRef.current = null
    followupsRef.current = []
    setDebugInfo(null)
    setLangfuseTraceId(null)
    setError(null)
  }, [resetTimers])

  api.haki.chat.useSubscription(chatInput ?? { threadId: 0 }, {
    enabled: !!chatInput,
    onStarted: () => startTimers(),
    onData: (chunk) => {
      if (activeThreadIdRef.current !== null && chatInput?.threadId !== activeThreadIdRef.current)
        return
      startTimers()
      if (chunk.type === "message_text_delta") {
        const visible = chunk.fullContent
          .split("\n")
          .filter((l) => !l.startsWith(">>SUGGESTION:"))
          .join("\n")
          .trimEnd()
        setStreamingText(visible)
        setToolStatus(undefined)
        setThinkingText("")
      } else if (chunk.type === "thinking_delta") {
        setThinkingText((prev) => prev + chunk.content)
        const last = reasoningRef.current[reasoningRef.current.length - 1]
        if (last?.type === "thinking") {
          last.content += chunk.content
          reasoningRef.current = [...reasoningRef.current]
        } else {
          reasoningRef.current = [
            ...reasoningRef.current,
            { type: "thinking", content: chunk.content },
          ]
        }
        setReasoningLog(reasoningRef.current)
      } else if (chunk.type === "tool_status") {
        setToolStatus({ tool: chunk.tool, detail: chunk.detail })
        setStreamingText("")
        setThinkingText("")
        reasoningRef.current = [
          ...reasoningRef.current,
          {
            type: "tool",
            content: chunk.tool,
            tool: chunk.tool,
            detail: chunk.detail,
            input: chunk.input,
          },
        ]
        setReasoningLog(reasoningRef.current)
      } else if (chunk.type === "block_committed") {
        const afterLength = (chunk as Record<string, unknown>).afterLength
        const block: CommittedBlock = {
          blockType: chunk.blockType as CommittedBlock["blockType"],
          blockId: chunk.blockId as string,
          data: (chunk as Record<string, unknown>).data,
          ...(typeof afterLength === "number" ? { afterLength } : {}),
        }
        blocksRef.current = [...blocksRef.current, block]
        setCommittedBlocks(blocksRef.current)
      } else if (chunk.type === "block_rejected") {
        console.warn("[useHakiStream] block rejected:", chunk.blockId, chunk.reason)
      } else if (chunk.type === "action_proposed") {
        const afterLength = (chunk as Record<string, unknown>).afterLength
        const block: CommittedBlock = {
          blockType: "action",
          blockId: chunk.blockId as string,
          data: { actionKind: chunk.actionKind, payload: chunk.payload },
          ...(typeof afterLength === "number" ? { afterLength } : {}),
        }
        blocksRef.current = [...blocksRef.current, block]
        setCommittedBlocks(blocksRef.current)
      } else if (chunk.type === "message_text_done") {
        finalContentRef.current = chunk.content
      } else if (chunk.type === "followups_committed") {
        followupsRef.current = chunk.followups
      } else if (chunk.type === "message_done") {
        resetTimers()
        const content = finalContentRef.current ?? ""
        const suggestions = followupsRef.current
        const info =
          "debugInfo" in chunk ? (chunk.debugInfo as HakiDebugInfo | undefined) : undefined
        setFinalContent(content)
        setFinalSuggestions(suggestions.length > 0 ? suggestions : undefined)
        if (info) setDebugInfo(info)
        setLangfuseTraceId(chunk.langfuseTraceId)
        setStreamingText("")
        setToolStatus(undefined)
        setThinkingText("")
        setChatInput(null)
        onComplete?.(
          content,
          suggestions.length > 0 ? suggestions : undefined,
          info,
          blocksRef.current.length > 0 ? blocksRef.current : undefined,
          reasoningRef.current.length > 0 ? reasoningRef.current : undefined,
        )
      } else if (chunk.type === "error") {
        resetTimers()
        const detail = "detail" in chunk ? (chunk.detail as string) : undefined
        if (detail) console.error("[useHakiStream] error detail:", detail)
        setError(chunk.content)
        setStreamingText("")
        setToolStatus(undefined)
        setThinkingText("")
        setChatInput(null)
        onError?.(chunk.content)
      }
    },
    onComplete: () => {
      if (finalContentRef.current !== null) return
      resetTimers()
      setStreamingText("")
      setToolStatus(undefined)
      setThinkingText("")
      setChatInput(null)
    },
    onError: (err) => {
      resetTimers()
      if (unloadingRef.current) return
      console.error("[useHakiStream] subscription error:", err)
      const errMsg = err instanceof Error ? err.message : String(err)
      const content =
        isHakio && errMsg
          ? `Something went wrong. Please try again. (${errMsg})`
          : "Something went wrong. Please try again."
      setError(content)
      setStreamingText("")
      setToolStatus(undefined)
      setThinkingText("")
      setChatInput(null)
      onError?.(content)
    },
  })

  const send = useCallback(
    (threadId: number, pageContext?: PageContext, source?: "chat" | "explain-forecast") => {
      activeThreadIdRef.current = threadId
      setError(null)
      setFinalContent(null)
      setFinalSuggestions(undefined)
      setCommittedBlocks([])
      blocksRef.current = []
      setReasoningLog([])
      reasoningRef.current = []
      finalContentRef.current = null
      followupsRef.current = []
      setDebugInfo(null)
      setLangfuseTraceId(null)
      setStreamingText("")
      setToolStatus(undefined)
      setThinkingText("")

      setChatInput({
        threadId,
        ...(pageContext ? { pageContext } : {}),
        numberFormatLocale: resolveLocaleForServer(),
        tone: resolveToneForServer(),
        ...(source ? { source } : {}),
      })
    },
    [],
  )

  return {
    send,
    streamingText,
    toolStatus,
    thinkingText,
    reasoningLog,
    isStreaming: !!chatInput,
    isComplete: finalContent !== null,
    stale,
    finalContent,
    finalSuggestions,
    committedBlocks,
    debugInfo,
    langfuseTraceId,
    error,
    cancel,
    reset,
  }
}
